import {
  styled,
  Grid,
  Stack,
  Typography,
  AccordionSummary,
  Chip,
  AccordionDetails,
  LinearProgress,
  LinearProgressProps,
  Link,
} from "@mui/material";
import React from "react";

import SmallAccordion from "../../Misc/SmallAccordion";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { StackTaskType } from "../../../Core/Api/Types/stack-task-type";
import { StackTaskKind } from "../../../Core/Api/Types/stack-task-kind";
import moment from "moment";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import OrderStatusIcon from "../../Misc/Status/OrderStatus/order-status";
import { ModalContext } from "../../Modals";

import ModalJobDetails, { ModalJobDetailsElementProps } from "../../Modals/ModalShowDetails/modal-job-details";
import ModalWorkflowDetails, {
  ModalWorkflowDetailsElementProps,
} from "../../Modals/ModalShowDetails/modal-workflow-details";
import { jobBrokerService, workflowService } from "../../../Core/Api/services";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";

export interface ConfigTaskCardProps {
  title: string;
  taskType: StackTaskType;
  taskKind: StackTaskKind;
  ressourceId: number;
  startDate: Date | null;
  endDate: Date | null;
  infos: string;
  progress: number;
  status: OrderStatus;
  defaultExpanded?: boolean;
}

const ConfigTaskCard: React.FC<ConfigTaskCardProps> = ({
  title,
  taskType,
  taskKind,
  ressourceId,
  startDate,
  endDate,
  infos,
  progress,
  status,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleShowDetails = () => {
    if (!openModal) return;

    if (taskType === StackTaskType.Job) {
      jobBrokerService.GetJob(ressourceId).then((response) => {
        if(response.job) {
        openModal<ModalJobDetailsElementProps>(ModalJobDetails, {
          job: response.job,
        });
        }
      });
    } else if (taskType === StackTaskType.Workflow) {
      workflowService.GetInstanceById(ressourceId).then((response) => {
        if(response.instance?.workflow) {
        openModal<ModalWorkflowDetailsElementProps>(ModalWorkflowDetails, {
            workflow: response.instance.workflow,
          });
        }
      });
    }
  };

  let color: LinearProgressProps["color"] = "success";

  if (status === OrderStatus.ABORTED || status === OrderStatus.FAILED) color = "error";
  else if (status === OrderStatus.RUNNING) color = "primary";
  else if (status === OrderStatus.CANCELED) color = "info";
  else if (status === OrderStatus.QUEUED || status === OrderStatus.CREATED || status === OrderStatus.READY)
    color = "info";

  return (
    <StyledAccordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary
        expandIcon={
          <>
            <AddIcon className="MuiAddIcon-root" />
            <RemoveIcon className="MuiRemoveIcon-root" />
          </>
        }
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip label={title} variant="outlined" color="primary" />
          <OrderStatusIcon status={status} iconOnly={true} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item md={4}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("common:taskTypeLowerCase")}
            </Typography>
            <Typography>{taskType}</Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("card:stackTaskCard.body.taskKind")}
            </Typography>
            <Typography>{taskKind}</Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("card:stackTaskCard.body.resourceId")}
            </Typography>
            <Typography>{ressourceId}</Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("common:startDateLowerCase")}
            </Typography>
            <Typography>
              {startDate ? moment(startDate!).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : "Not started"}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("common:endDateLowerCase")}
            </Typography>
            <Typography>
              {endDate ? moment(endDate!).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : "Not finished"}
            </Typography>
          </Grid>
          {UserHasAcl(AclTypes.aclSuperUser) && (
            <Grid item md={4}>
              <Typography variant="body2" fontWeight={500} color="text.tier">
                {t("common:details")}
              </Typography>
              <Typography>
                <Link href="#" underline="hover" variant="body1" onClick={handleShowDetails}>
                  {t("card:stackTaskCard.body.openDetails")}
                </Link>
              </Typography>
            </Grid>
          )}

          <Grid item md={12}>
            <Typography variant="body2" fontWeight={500} color="text.tier">
              {t("common:infos")}
            </Typography>
            <Typography>
              {infos} ({progress}%)
            </Typography>
          </Grid>
        </Grid>
        <Stack direction="row" alignItems="center" mt={3} spacing={2}>
          <StyledLinearProgress variant="determinate" value={progress} color={color} />
          <Stack display="inline-flex" fontWeight={500}>
            <OrderStatusIcon status={status} />
          </Stack>
        </Stack>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default ConfigTaskCard;

const StyledAccordion = styled(SmallAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  ".MuiRemoveIcon-root": {
    display: "none",
  },
  ".Mui-expanded": {
    ".MuiRemoveIcon-root": {
      display: "block",
    },
    ".MuiAddIcon-root": {
      display: "none",
    },
    ".MuiCheckCircleIcon-root": {
      display: "none",
    },
  },
  ".MuiAccordionDetails-root": {
    border: "none",
    paddingTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  ".MuiAccordionSummary-root": {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  width: "100%",
  borderRadius: 7,
}));
